import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import type { NotificationProviderDto } from "@/features/notification/api/dto";
import { NotificationProviderRowActions } from "@/features/notification/components/providers/notification-provider-row-actions";
import {
  NOTIFICATION_CHANNELS,
  type NotificationChannel,
} from "@/features/shared/api/enums";

export type NotificationProviderRow = NotificationProviderDto;

const selectOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value }));

const CHANNEL_VARIANT: Record<
  NotificationChannel,
  "default" | "secondary" | "outline"
> = {
  SMS: "default",
  EMAIL: "secondary",
  PUSH: "outline",
};

/** Colonnes du registre `NotificationProviders` (guide v2.3 §6.7). */
export const notificationProvidersColumns: ColumnsClarFactory<
  NotificationProviderRow
> = (navigate) => [
  {
    id: "code",
    accessorKey: "code",
    header: t("notifications.providers.columns.code"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono font-semibold">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "name",
    accessorKey: "name",
    header: t("notifications.providers.columns.name"),
    cell: ({ getValue }) => (
      <span className="text-sm">{(getValue() as string) || "—"}</span>
    ),
    meta: { type: "denied" },
  },
  {
    id: "channel",
    accessorKey: "channel",
    header: t("notifications.providers.columns.channel"),
    cell: ({ getValue }) => {
      const channel = getValue() as NotificationChannel | undefined;
      return channel ? (
        <NoTranslate as="span">
          <Badge variant={CHANNEL_VARIANT[channel] ?? "outline"}>
            {channel}
          </Badge>
        </NoTranslate>
      ) : (
        "—"
      );
    },
    meta: { type: "select", options: selectOptions(NOTIFICATION_CHANNELS) },
  },
  {
    id: "countryCode",
    accessorKey: "countryCode",
    header: t("notifications.providers.columns.countryCode"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string | null | undefined) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "weight",
    accessorKey: "weight",
    header: t("notifications.providers.columns.weight"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-medium tabular-nums">
        {String(getValue() ?? "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "priority",
    accessorKey: "priority",
    header: t("notifications.providers.columns.priority"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-medium tabular-nums">
        {String(getValue() ?? "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "isActive",
    accessorKey: "isActive",
    header: t("notifications.providers.columns.isActive"),
    cell: ({ getValue }) => {
      const active = Boolean(getValue());
      return (
        <Badge variant={active ? "success" : "destructive"}>
          {active
            ? t("notifications.templates.status.active")
            : t("notifications.templates.status.inactive")}
        </Badge>
      );
    },
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <NotificationProviderRowActions
          provider={row.original}
          navigate={navigate}
        />
      ) : null,
    meta: { type: "denied" },
  },
];
